import { useEffect, useState, useSyncExternalStore } from 'react';
import { Box, IconButton, LinearProgress, Paper, Slide, Tooltip, Typography } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CancelIcon from '@mui/icons-material/Cancel';
import { keyframes } from '@emotion/react';
import {
  cancelCodexActivity,
  getCodexActivitySnapshot,
  subscribeCodexActivity,
} from '../ai/activity';
import { isHostedWeb } from '../config/cloud';

// Gentle pulse for the sparkle icon while Codex is generating.
const pulse = keyframes`
  0%, 100% { opacity: 0.5; transform: scale(0.9); }
  50% { opacity: 1; transform: scale(1.1); }
`;

// Classic three-dot "typing/thinking" bounce.
const bounce = keyframes`
  0%, 80%, 100% { transform: translateY(0); opacity: 0.35; }
  40% { transform: translateY(-5px); opacity: 1; }
`;

function ThinkingDots() {
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'flex-end', gap: 0.5, ml: 0.25 }}>
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          component="span"
          sx={{
            width: 5,
            height: 5,
            borderRadius: '50%',
            bgcolor: 'primary.main',
            animation: `${bounce} 1.2s ${i * 0.16}s ease-in-out infinite`,
          }}
        />
      ))}
    </Box>
  );
}

function fmtElapsed(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return m > 0 ? `${m}:${s.toString().padStart(2, '0')}` : `${s}s`;
}

/**
 * One shared "Codex is thinking…" animation for the whole app. It listens to the
 * global activity tracker (src/ai/activity.ts) and shows itself whenever ANY
 * Codex generation is in flight. Alongside the label it shows elapsed time (so
 * "slow" is distinguishable from "hung"), the last streamed output line, and a
 * cancel button that kills the CLI process.
 */
export default function CodexActivityIndicator() {
  const snap = useSyncExternalStore(
    subscribeCodexActivity,
    getCodexActivitySnapshot,
    getCodexActivitySnapshot,
  );

  // Tick every second while active so the elapsed label counts up.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!snap.active) return;
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, [snap.active]);

  const elapsed = snap.startedAt ? fmtElapsed(Date.now() - snap.startedAt) : null;

  return (
    <>
      {/* Indeterminate progress bar pinned to the very top of the window. */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          zIndex: (t) => t.zIndex.tooltip + 1,
          pointerEvents: 'none',
        }}
      >
        {snap.active && <LinearProgress color="secondary" sx={{ height: 3 }} />}
      </Box>

      {/* Floating "thinking" pill, bottom-center so it clears the toast snackbar. */}
      <Slide in={snap.active} direction="up" mountOnEnter unmountOnExit>
        <Paper
          elevation={6}
          aria-live="polite"
          sx={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: (t) => t.zIndex.snackbar,
            px: 2,
            py: 1,
            borderRadius: 4,
            border: '1px solid',
            borderColor: 'divider',
            maxWidth: 560,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AutoAwesomeIcon
              fontSize="small"
              color="primary"
              sx={{ animation: `${pulse} 1.4s ease-in-out infinite` }}
            />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {snap.label ?? (isHostedWeb() ? 'AI is thinking…' : 'Codex is thinking…')}
            </Typography>
            <ThinkingDots />
            {elapsed && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5, fontVariantNumeric: 'tabular-nums' }}>
                {elapsed}
              </Typography>
            )}
            {snap.count > 1 && (
              <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                +{snap.count - 1} more
              </Typography>
            )}
            {snap.cancellable && (
              <Tooltip title="Cancel this AI call">
                <IconButton size="small" onClick={cancelCodexActivity} sx={{ ml: 0.5 }}>
                  <CancelIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          {/* Raw CLI output is an internal diagnostic — desktop builds only. */}
          {snap.tail && !isHostedWeb() && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: 'block',
                mt: 0.5,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                fontFamily: 'monospace',
              }}
            >
              {snap.tail}
            </Typography>
          )}
        </Paper>
      </Slide>
    </>
  );
}
